import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button, Modal, FormInput, Toggle, FormMultiSelect, ConfirmDialog } from '../../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  // Gaming Core
  Gamepad2, Joystick, Dice5, Dice6, Ghost, Swords, Puzzle, Target, Zap, Crosshair,
  // Casino & Cards
  Spade, Club, Diamond, Heart, Crown, Trophy, Medal, Gift, Sparkles, Star,
  Shuffle, RotateCcw, Gem, Coins, CircleDot,
  // Popular
  Flame, Rocket, TrendingUp, Activity, FastForward, Timer, Radar, Magnet, Goal,
  // Fishing / Nature
  Fish, Waves, Anchor, Shell, Leaf, Mountain, TreePine, Sunset, Wind, Feather,
  // Crash / Action
  Bomb, TrendingDown, AlertTriangle, Navigation, Gauge, BarChart2, LineChart,
  // Slots / Arcade
  LayoutGrid, Layers, Monitor, Cpu, Smartphone, Grid3X3, Gem as GemIcon,
  PlayCircle, CirclePlay, Tv2, ScanLine, MonitorPlay, Video, Tv,
  // Lottery
  Ticket, Hash, Percent, DollarSign, BadgeDollarSign, Banknote, CreditCard, Landmark,
  // Esports / Video Games
  Headset, Terminal, Code2, Wifi, Radio, Mic2, Layers3,
  // Sports
  Dumbbell, Flag, Users, Globe, Shield, Compass, Map, Plane, Car, Ship, Bike,
  // P2P / Virtual
  ArrowLeftRight, RefreshCcw, Network, GitBranch,
  // Misc
  Music, Bell, Camera, Image, Key, Wallet, Lock, Sun, Moon, Cloud,
  Snowflake, Lightbulb, Eye, Search, Headphones, Mic, MessageSquare, Settings
} from 'lucide-react';

const fetchProvidersAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/providers?show_all=true', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

const fetchAggregatorsAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/aggregators', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

interface Group {
  id: number;
  name: string;
  sort_order: number;
  admin_is_enabled: boolean;
  show_on_home_index?: boolean;
  show_on_home_casino?: boolean;
  show_as_tab_casino?: boolean;
  show_on_home_sports?: boolean;
  show_as_tab_sports?: boolean;
  icon?: string;
}

const ICON_CATEGORIES = [
  {
    label: '🎮 Gaming Core',
    icons: [
      { id: 'Gamepad2', icon: Gamepad2 },
      { id: 'Joystick', icon: Joystick },
      { id: 'Dice5', icon: Dice5 },
      { id: 'Dice6', icon: Dice6 },
      { id: 'Ghost', icon: Ghost },
      { id: 'Swords', icon: Swords },
      { id: 'Puzzle', icon: Puzzle },
      { id: 'Target', icon: Target },
      { id: 'Zap', icon: Zap },
      { id: 'Crosshair', icon: Crosshair },
    ]
  },
  {
    label: '🃏 Casino & Cards',
    icons: [
      { id: 'Spade', icon: Spade },
      { id: 'Club', icon: Club },
      { id: 'Diamond', icon: Diamond },
      { id: 'Heart', icon: Heart },
      { id: 'Shuffle', icon: Shuffle },
      { id: 'RotateCcw', icon: RotateCcw },
      { id: 'Gem', icon: Gem },
      { id: 'Crown', icon: Crown },
      { id: 'Coins', icon: Coins },
      { id: 'CircleDot', icon: CircleDot },
    ]
  },
  {
    label: '🎴 Poker & Table Games',
    icons: [
      { id: 'Gem', icon: Gem },
      { id: 'Layers', icon: Layers },
      { id: 'Layers3', icon: Layers3 },
      { id: 'Grid3X3', icon: Grid3X3 },
      { id: 'LayoutGrid', icon: LayoutGrid },
      { id: 'CirclePlay', icon: CirclePlay },
      { id: 'ScanLine', icon: ScanLine },
      { id: 'Shuffle', icon: Shuffle },
      { id: 'Star', icon: Star },
      { id: 'Medal', icon: Medal },
    ]
  },
  {
    label: '🎰 Slots / Arcade',
    icons: [
      { id: 'LayoutGrid', icon: LayoutGrid },
      { id: 'Grid3X3', icon: Grid3X3 },
      { id: 'Monitor', icon: Monitor },
      { id: 'Tv', icon: Tv },
      { id: 'Tv2', icon: Tv2 },
      { id: 'PlayCircle', icon: PlayCircle },
      { id: 'Video', icon: Video },
      { id: 'MonitorPlay', icon: MonitorPlay },
      { id: 'ScanLine', icon: ScanLine },
      { id: 'Cpu', icon: Cpu },
    ]
  },
  {
    label: '🎟️ Lottery & Chance',
    icons: [
      { id: 'Ticket', icon: Ticket },
      { id: 'Hash', icon: Hash },
      { id: 'Percent', icon: Percent },
      { id: 'DollarSign', icon: DollarSign },
      { id: 'BadgeDollarSign', icon: BadgeDollarSign },
      { id: 'Banknote', icon: Banknote },
      { id: 'CreditCard', icon: CreditCard },
      { id: 'Landmark', icon: Landmark },
      { id: 'Sparkles', icon: Sparkles },
      { id: 'Gift', icon: Gift },
    ]
  },
  {
    label: '💥 Crash / Action',
    icons: [
      { id: 'Bomb', icon: Bomb },
      { id: 'TrendingDown', icon: TrendingDown },
      { id: 'AlertTriangle', icon: AlertTriangle },
      { id: 'Gauge', icon: Gauge },
      { id: 'BarChart2', icon: BarChart2 },
      { id: 'LineChart', icon: LineChart },
      { id: 'FastForward', icon: FastForward },
      { id: 'Navigation', icon: Navigation },
      { id: 'Timer', icon: Timer },
      { id: 'Rocket', icon: Rocket },
    ]
  },
  {
    label: '🎣 Fishing / Nature',
    icons: [
      { id: 'Fish', icon: Fish },
      { id: 'Waves', icon: Waves },
      { id: 'Anchor', icon: Anchor },
      { id: 'Shell', icon: Shell },
      { id: 'Leaf', icon: Leaf },
      { id: 'Mountain', icon: Mountain },
      { id: 'TreePine', icon: TreePine },
      { id: 'Sunset', icon: Sunset },
      { id: 'Wind', icon: Wind },
      { id: 'Feather', icon: Feather },
    ]
  },
  {
    label: '🖥️ Esports & Video Games',
    icons: [
      { id: 'MonitorPlay', icon: MonitorPlay },
      { id: 'Headset', icon: Headset },
      { id: 'Terminal', icon: Terminal },
      { id: 'Code2', icon: Code2 },
      { id: 'Wifi', icon: Wifi },
      { id: 'Smartphone', icon: Smartphone },
      { id: 'Mic2', icon: Mic2 },
      { id: 'Radio', icon: Radio },
      { id: 'Joystick', icon: Joystick },
      { id: 'Gamepad2', icon: Gamepad2 },
    ]
  },
  {
    label: '🔀 P2P / Virtual Sport',
    icons: [
      { id: 'ArrowLeftRight', icon: ArrowLeftRight },
      { id: 'RefreshCcw', icon: RefreshCcw },
      { id: 'Network', icon: Network },
      { id: 'GitBranch', icon: GitBranch },
      { id: 'Globe', icon: Globe },
      { id: 'Shield', icon: Shield },
      { id: 'Users', icon: Users },
      { id: 'Layers3', icon: Layers3 },
      { id: 'Shuffle', icon: Shuffle },
      { id: 'Compass', icon: Compass },
    ]
  },
  {
    label: '⚽ Sports / Competition',
    icons: [
      { id: 'Goal', icon: Goal },
      { id: 'Flag', icon: Flag },
      { id: 'Trophy', icon: Trophy },
      { id: 'Dumbbell', icon: Dumbbell },
      { id: 'Bike', icon: Bike },
      { id: 'Globe', icon: Globe },
      { id: 'Magnet', icon: Magnet },
      { id: 'Radar', icon: Radar },
      { id: 'Activity', icon: Activity },
      { id: 'TrendingUp', icon: TrendingUp },
    ]
  },
  {
    label: '🔥 Popular / Trending',
    icons: [
      { id: 'Flame', icon: Flame },
      { id: 'Sparkles', icon: Sparkles },
      { id: 'Star', icon: Star },
      { id: 'Trophy', icon: Trophy },
      { id: 'Crown', icon: Crown },
      { id: 'Medal', icon: Medal },
      { id: 'Gift', icon: Gift },
      { id: 'Bell', icon: Bell },
      { id: 'Eye', icon: Eye },
      { id: 'Search', icon: Search },
    ]
  },
  {
    label: '🔧 UI / Other',
    icons: [
      { id: 'Music', icon: Music },
      { id: 'Camera', icon: Camera },
      { id: 'Wallet', icon: Wallet },
      { id: 'Key', icon: Key },
      { id: 'Lock', icon: Lock },
      { id: 'Sun', icon: Sun },
      { id: 'Moon', icon: Moon },
      { id: 'Cloud', icon: Cloud },
      { id: 'Snowflake', icon: Snowflake },
      { id: 'Lightbulb', icon: Lightbulb },
    ]
  },
];

type IconEntry = { id: string; icon: React.FC<any> };

const allIcons: IconEntry[] = ICON_CATEGORIES.flatMap(cat => cat.icons);
const seen = new Set<string>();
const COMMON_ICONS: IconEntry[] = allIcons.filter(i => {
  if (seen.has(i.id)) return false;
  seen.add(i.id);
  return true;
});


const fetchGroupsAPI = async (): Promise<Group[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/groups`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: groups = [], isLoading } = useQuery({ queryKey: ['groups'], queryFn: fetchGroupsAPI });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Group>>({});
  const [isEdit, setIsEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [confirmDeleteGroupId, setConfirmDeleteGroupId] = useState<number | null>(null);
  const [confirmClearGroupId, setConfirmClearGroupId] = useState<number | null>(null);
  const [confirmClearGroupName, setConfirmClearGroupName] = useState<string>('');

  const [manageGamesGroupId, setManageGamesGroupId] = useState<number | null>(null);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [providerAggFilter, setProviderAggFilter] = useState<string>('');
  
  const { data: providers = [] } = useQuery({ queryKey: ['providers-all'], queryFn: fetchProvidersAPI });
  const { data: aggregators = [] } = useQuery({ queryKey: ['aggregators'], queryFn: fetchAggregatorsAPI });
  
  const assignMutation = useMutation({
    mutationFn: async (provider_ids: string[]) => {
      const token = localStorage.getItem('token');
      return apiClient.post(`/api/game-engine/admin/groups/${manageGamesGroupId}/assign-games`, { provider_ids }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      toast.success(res.data.message || 'Games assigned to group!');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setManageGamesGroupId(null);
      setSelectedProviderIds([]);
    },
    onError: () => toast.error('Failed to assign games')
  });

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Group>) => {
      const token = localStorage.getItem("token");
      if (data.id) return apiClient.put(`/api/game-engine/admin/groups/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      return apiClient.post(`/api/game-engine/admin/groups`, data, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success(variables.id ? "Group updated!" : "Group created!");
      closeModal();
    },
    onError: () => toast.error("Failed to save group")
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem("token");
      return apiClient.delete(`/api/game-engine/admin/groups/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success("Group deleted!");
    },
    onError: () => toast.error("Failed to delete group")
  });

  const clearGamesMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem("token");
      return apiClient.delete(`/api/game-engine/admin/groups/${id}/clear-games`, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success("All games removed from group successfully!");
    },
    onError: () => toast.error("Failed to remove games from group")
  });

  const openModal = (grp?: Group) => {
    if (grp) { setFormData(grp); setIsEdit(true); }
    else { 
      setFormData({ 
        name: '', sort_order: 0, admin_is_enabled: true,
        show_on_home_index: false, show_on_home_casino: false, show_as_tab_casino: false,
        show_on_home_sports: false, show_as_tab_sports: false,
        icon: 'Gamepad2'
      }); 
      setIsEdit(false); 
    }
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setFormData({}); };
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); saveMutation.mutate(formData); };

  const filtered = groups.filter((a: any) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const paginated = filtered.slice((page - 1) * limit, page * limit);

  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { 
      key: 'name', 
      label: 'Name',
      render: (val: string, item: Group) => {
        const IconData = COMMON_ICONS.find(i => i.id === item.icon);
        const IconComponent = IconData ? IconData.icon : null;
        return (
          <div className="flex items-center gap-2">
            {IconComponent && <IconComponent size={16} className="text-indigo-400 shrink-0" />}
            <span onClick={() => navigate(`/game-engine/games?group_id=${item.id}`)} className="font-semibold text-slate-200 hover:text-indigo-400 hover:underline cursor-pointer">
              {val}
            </span>
          </div>
        );
      }
    },
    { key: 'game_count', label: 'Games Count', render: (val: number, item: Group) => (
      <span onClick={() => navigate(`/game-engine/games?group_id=${item.id}`)} className="font-bold text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer">
        {val || 0}
      </span>
    ) },
    { key: 'sort_order', label: 'Sort Order' },
    {
      key: 'admin_is_enabled', label: 'Admin Status',
      render: (val: boolean, item: Group) => <Toggle checked={val} onChange={(c) => saveMutation.mutate({ ...item, admin_is_enabled: c })} />
    },
    {
      key: 'actions', label: 'Actions', textAlign: 'right' as const,
      render: (_: any, item: Group) => (
        <div className="flex items-center justify-end gap-2">
          <Button variant="primary" size="xs" onClick={() => setManageGamesGroupId(item.id)}>Manage Games</Button>
          <Button variant="ghost" size="xs" onClick={() => openModal(item)}>Edit</Button>
          <Button variant="danger" size="xs" onClick={() => setConfirmDeleteGroupId(item.id)}>Delete</Button>
          <Button variant="warning" size="xs" onClick={() => {
            setConfirmClearGroupId(item.id);
            setConfirmClearGroupName(item.name);
          }}>Clear Games</Button>
        </div>
      )
    }
  ];

  return (
    <>
      <StakeTable
        title="Game Groups"
        columns={columns}
        data={paginated}
        isLoading={isLoading}
        searchPlaceholder="Search groups..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={filtered.length}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No groups found."
        actions={<Button variant="success" onClick={() => openModal()}>+ Add Group</Button>}
      />

            <Modal open={isModalOpen} onClose={closeModal} title={isEdit ? 'Edit Group' : 'Add Group'}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" size="sm" loading={saveMutation.isPending} onClick={handleSubmit}>{isEdit ? 'Save Changes' : 'Create'}</Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <FormInput type="number" label="Sort Order" required value={formData.sort_order || 0} onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })} />
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Group Icon Selection</label>
            <div className="max-h-56 overflow-y-auto space-y-3 p-3 bg-slate-950/50 border border-slate-700/50 rounded-lg">
              {ICON_CATEGORIES.map((cat) => (
                <div key={cat.label}>
                  <div className="text-xs text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">{cat.label}</div>
                  <div className="grid grid-cols-10 gap-1.5">
                    {cat.icons.map((item) => {
                      const IconComponent = item.icon;
                      const isSelected = formData.icon === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon: item.id })}
                          title={item.id}
                          className={`aspect-square p-1.5 rounded-md flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30 scale-110 ring-1 ring-indigo-400'
                              : 'bg-slate-900 border border-slate-800 text-slate-400 hover:border-indigo-500/50 hover:text-white hover:bg-slate-800'
                          }`}
                        >
                          <IconComponent size={14} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {formData.icon && (
              <div className="flex items-center gap-2 mt-1 text-xs text-indigo-400 font-bold uppercase tracking-wider">
                ✓ Selected: {formData.icon}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show on Home Index</span>
                <Toggle checked={!!formData.show_on_home_index} onChange={c => setFormData({...formData, show_on_home_index: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show on Casino Home</span>
                <Toggle checked={!!formData.show_on_home_casino} onChange={c => setFormData({...formData, show_on_home_casino: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show as Casino Tab</span>
                <Toggle checked={!!formData.show_as_tab_casino} onChange={c => setFormData({...formData, show_as_tab_casino: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show on Sports Home</span>
                <Toggle checked={!!formData.show_on_home_sports} onChange={c => setFormData({...formData, show_on_home_sports: c})} />
            </div>
            <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-slate-800">
                <span className="text-sm text-gray-300">Show as Sports Tab</span>
                <Toggle checked={!!formData.show_as_tab_sports} onChange={c => setFormData({...formData, show_as_tab_sports: c})} />
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={manageGamesGroupId !== null} onClose={() => setManageGamesGroupId(null)} title="Assign Games to Group"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setManageGamesGroupId(null)}>Cancel</Button>
            <Button 
              variant="primary" 
              size="sm" 
              disabled={selectedProviderIds.length === 0} 
              loading={assignMutation.isPending} 
              onClick={() => assignMutation.mutate(selectedProviderIds)}
            >
              Assign Provider Games
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-300">Select one or more Providers to bulk-assign all their active games into this group. You can also assign games manually via the Games Management page.</p>
          
          {/* Aggregator Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium whitespace-nowrap">Filter by Aggregator:</span>
            <select
              className="flex-1 bg-slate-900 border border-slate-700 text-slate-200 rounded-md px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500 transition-all"
              value={providerAggFilter}
              onChange={e => { setProviderAggFilter(e.target.value); setSelectedProviderIds([]); }}
            >
              <option value="">All Aggregators</option>
              {aggregators.map((a: any) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
            {providerAggFilter && (
              <button
                onClick={() => { setProviderAggFilter(''); setSelectedProviderIds([]); }}
                className="text-xs text-slate-400 hover:text-slate-200 transition-colors px-1"
                title="Clear filter"
              >✕</button>
            )}
          </div>

          <div className="pb-32">
            <FormMultiSelect
              label="Providers"
              placeholder="Search and Select Providers"
              options={providers
                .filter((p: any) => p.developer_is_enabled)
                .filter((p: any) => providerAggFilter ? String(p.aggregator_id) === providerAggFilter : true)
                .map((prov: any) => ({
                  value: String(prov.id),
                  label: prov.aggregator_name ? `${prov.name} — ${prov.aggregator_name}` : prov.name
                }))}
              selectedValues={selectedProviderIds}
              onChange={setSelectedProviderIds}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDeleteGroupId !== null}
        onClose={() => setConfirmDeleteGroupId(null)}
        onConfirm={() => {
          if (confirmDeleteGroupId !== null) {
            deleteMutation.mutate(confirmDeleteGroupId, {
              onSuccess: () => setConfirmDeleteGroupId(null)
            });
          }
        }}
        title="Delete Group"
        message="Are you sure you want to delete this group?"
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={confirmClearGroupId !== null}
        onClose={() => {
          setConfirmClearGroupId(null);
          setConfirmClearGroupName('');
        }}
        onConfirm={() => {
          if (confirmClearGroupId !== null) {
            clearGamesMutation.mutate(confirmClearGroupId, {
              onSuccess: () => {
                setConfirmClearGroupId(null);
                setConfirmClearGroupName('');
              }
            });
          }
        }}
        title="Clear Group Games"
        message={`Are you sure you want to remove all games from the group "${confirmClearGroupName}"?`}
        confirmLabel="Clear"
        confirmVariant="warning"
        loading={clearGamesMutation.isPending}
      />
    </>
  );
};
export default Groups;
